import { useEffect, useRef, useState } from "react";
import { errorMsg, getJson, putJson } from "../api";

import StepPcbProvide from "./StepPcbProvide";
import StepPcbOrder from "./StepPcbOrder";

function hasMeaningfulValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

const RESTORED_SELECTION_GROUPS = [
  ["family"],
  ["material"],
  ["surface_finish", "finish"],
  ["layers"],
  ["thickness"],
  ["copper_external", "external_copper_thickness"],
  ["copper_internal", "inner_copper_thickness"],
  ["stackup_method", "stackup", "stackup_type"],
  ["silkscreen_color", "silkscreen_ink_color", "silkscreen"],
  ["color_top", "solder_color_top"],
  ["color_bottom", "color_bot", "solder_color_bot"],
  ["soldermask_position", "position"],
  ["legend"],
  ["date_position"],
  ["pcb_supplier", "supplier"],
  ["ipc_class", "ipc"],
];

const RESTORED_SELECTION_EXTRA_KEYS = new Set([
  "size_x",
  "size_y",
  "pcb_quantity",
  "quote_mode",
  "manual_note",
  "manual_reason",
]);

function mergeSelectionCandidates(...candidates) {
  const out = {};
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
    for (const [key, value] of Object.entries(candidate)) {
      if (!hasMeaningfulValue(value)) continue;
      if (!hasMeaningfulValue(out[key])) out[key] = value;
    }
  }
  return out;
}

function getBackendSelectedSeed(pcb) {
  const bundle = pcb?.safe_pcb_bundle || {};
  const merged = mergeSelectionCandidates(
    pcb?.selected_choices,
    bundle?.selected_choices,
    bundle?.selected_clean
  );
  const out = {};

  RESTORED_SELECTION_GROUPS.forEach((variants) => {
    const canonicalKey = variants[0];
    const foundKey = variants.find((variant) => hasMeaningfulValue(merged?.[variant]));
    if (foundKey) out[canonicalKey] = merged[foundKey];
  });

  RESTORED_SELECTION_EXTRA_KEYS.forEach((key) => {
    if (hasMeaningfulValue(merged?.[key])) out[key] = merged[key];
  });

  return out;
}

function hasServerSelectionSeed(pcb, selectedSeed) {
  if (Object.keys(selectedSeed || {}).length > 0) return true;
  const directFields = [
    pcb?.size_x,
    pcb?.size_y,
    pcb?.pcb_quantity,
    pcb?.layers,
    pcb?.material,
    pcb?.thickness,
    pcb?.copper_external,
    pcb?.external_copper_thickness,
    pcb?.copper_internal,
    pcb?.inner_copper_thickness,
    pcb?.surface_finish,
    pcb?.finish,
    pcb?.stackup_method,
    pcb?.stackup,
    pcb?.stackup_type,
    pcb?.silkscreen_color,
    pcb?.silkscreen_ink_color,
    pcb?.color_top,
    pcb?.solder_color_top,
    pcb?.color_bottom,
    pcb?.color_bot,
    pcb?.solder_color_bot,
    pcb?.soldermask_position,
    pcb?.position,
    pcb?.legend,
    pcb?.date_position,
    pcb?.pcb_supplier,
    pcb?.supplier,
    pcb?.ipc_class,
    pcb?.ipc,
    pcb?.manual_quote_note,
  ];
  return directFields.some(hasMeaningfulValue);
}

export default function StepPcb({ projectId, onNext, onPrev, onModeChange, projectQty }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [childBusy, setChildBusy] = useState(false);

  const [pcbMode, setPcbMode] = useState("order");
  const [selected, setSelected] = useState({});
  const [allowDraftRestore, setAllowDraftRestore] = useState(null);
  const [initialOrderState, setInitialOrderState] = useState(null);
  const [asmCounts, setAsmCounts] = useState({ cms_component_count: "", tht_component_count: "", pressfit_component_count: "" });

  const pcbOrderRef = useRef(null);
  const pcbProvideRef = useRef(null);

  useEffect(() => {
    if (pcbMode !== "order") setChildBusy(false);
  }, [pcbMode]);

  useEffect(() => {
    let mounted = true;

    async function boot() {
      setLoading(true);
      setAllowDraftRestore(null);
      setInitialOrderState(null);
      try {
        const cfg = await getJson(`/user/projects/${projectId}/pcb-config/`);
        const pcb = cfg?.pcb_spec || {};
        const asm = cfg?.assembly_spec || {};
        const bundle = pcb?.safe_pcb_bundle && typeof pcb.safe_pcb_bundle === "object" ? pcb.safe_pcb_bundle : null;

        const modeFromPcb = (pcb?.client_pcb_mode || "").toLowerCase();
        const provide = Boolean(asm?.customer_supplies_pcb) || modeFromPcb === "provide";

        const backendSelectedSeed = getBackendSelectedSeed(pcb);
        const hasServerSeed = hasServerSelectionSeed(pcb, backendSelectedSeed);
        const initialSelected = { ...backendSelectedSeed };
        if (pcb?.size_x && !initialSelected.size_x) initialSelected.size_x = String(pcb.size_x);
        if (pcb?.size_y && !initialSelected.size_y) initialSelected.size_y = String(pcb.size_y);
        if (pcb?.pcb_quantity && !initialSelected.pcb_quantity) initialSelected.pcb_quantity = String(pcb.pcb_quantity);
        if (pcb?.layers && !initialSelected.layers) initialSelected.layers = String(pcb.layers);
        if (pcb?.material && !initialSelected.material) initialSelected.material = String(pcb.material);
        if (pcb?.thickness && !initialSelected.thickness) initialSelected.thickness = String(pcb.thickness);
        if (pcb?.finish && !initialSelected.surface_finish && !initialSelected.finish) {
          initialSelected.surface_finish = pcb.finish;
        }
        if (pcb?.surface_finish && !initialSelected.surface_finish && !initialSelected.finish) {
          initialSelected.surface_finish = pcb.surface_finish;
        }
        if (pcb?.external_copper_thickness && !initialSelected.copper_external && !initialSelected.external_copper_thickness) {
          initialSelected.copper_external = String(pcb.external_copper_thickness);
        }
        if (pcb?.copper_external && !initialSelected.copper_external && !initialSelected.external_copper_thickness) {
          initialSelected.copper_external = String(pcb.copper_external);
        }
        if (pcb?.inner_copper_thickness && !initialSelected.copper_internal && !initialSelected.inner_copper_thickness) {
          initialSelected.copper_internal = String(pcb.inner_copper_thickness);
        }
        if (pcb?.copper_internal && !initialSelected.copper_internal && !initialSelected.inner_copper_thickness) {
          initialSelected.copper_internal = String(pcb.copper_internal);
        }
        if (pcb?.stackup_type && !initialSelected.stackup_method && !initialSelected.stackup && !initialSelected.stackup_type) {
          initialSelected.stackup_method = String(pcb.stackup_type);
        }
        if (pcb?.stackup_method && !initialSelected.stackup_method && !initialSelected.stackup && !initialSelected.stackup_type) {
          initialSelected.stackup_method = String(pcb.stackup_method);
        }
        if (pcb?.stackup && !initialSelected.stackup_method && !initialSelected.stackup && !initialSelected.stackup_type) {
          initialSelected.stackup_method = String(pcb.stackup);
        }
        if (pcb?.silkscreen_ink_color && !initialSelected.silkscreen_color && !initialSelected.silkscreen_ink_color) {
          initialSelected.silkscreen_color = String(pcb.silkscreen_ink_color);
        }
        if (pcb?.silkscreen_color && !initialSelected.silkscreen_color && !initialSelected.silkscreen_ink_color) {
          initialSelected.silkscreen_color = String(pcb.silkscreen_color);
        }
        if (pcb?.solder_color_top && !initialSelected.color_top && !initialSelected.solder_color_top) {
          initialSelected.color_top = String(pcb.solder_color_top);
        }
        if (pcb?.color_top && !initialSelected.color_top && !initialSelected.solder_color_top) {
          initialSelected.color_top = String(pcb.color_top);
        }
        if (pcb?.solder_color_bot && !initialSelected.color_bottom && !initialSelected.color_bot && !initialSelected.solder_color_bot) {
          initialSelected.color_bottom = String(pcb.solder_color_bot);
        }
        if (pcb?.color_bottom && !initialSelected.color_bottom && !initialSelected.color_bot && !initialSelected.solder_color_bot) {
          initialSelected.color_bottom = String(pcb.color_bottom);
        }
        if (pcb?.color_bot && !initialSelected.color_bottom && !initialSelected.color_bot && !initialSelected.solder_color_bot) {
          initialSelected.color_bottom = String(pcb.color_bot);
        }
        if (pcb?.soldermask_position && !initialSelected.soldermask_position && !initialSelected.position) {
          initialSelected.soldermask_position = String(pcb.soldermask_position);
        }
        if (pcb?.position && !initialSelected.soldermask_position && !initialSelected.position) {
          initialSelected.soldermask_position = String(pcb.position);
        }
        if (pcb?.pcb_supplier && !initialSelected.pcb_supplier && !initialSelected.supplier) {
          initialSelected.pcb_supplier = String(pcb.pcb_supplier);
        }
        if (pcb?.supplier && !initialSelected.pcb_supplier && !initialSelected.supplier) {
          initialSelected.pcb_supplier = String(pcb.supplier);
        }
        if (pcb?.ipc_class && !initialSelected.ipc_class && !initialSelected.ipc) {
          initialSelected.ipc_class = String(pcb.ipc_class);
        }
        if (pcb?.ipc && !initialSelected.ipc_class && !initialSelected.ipc) {
          initialSelected.ipc_class = String(pcb.ipc);
        }
        if (pcb?.manual_quote_note && !initialSelected.manual_note) {
          initialSelected.manual_note = String(pcb.manual_quote_note);
          initialSelected.quote_mode = initialSelected.quote_mode || "MANUAL";
        }
        if (projectQty && projectQty > 0) {
          initialSelected.pcb_quantity = String(projectQty);
        } else if (!initialSelected.pcb_quantity) {
          initialSelected.pcb_quantity = "1";
        }
        if (!initialSelected.family) initialSelected.family = "RIGID";
        if (!initialSelected.material) initialSelected.material = "S1000H";
        if (!initialSelected.surface_finish && !initialSelected.finish) initialSelected.surface_finish = "CHEM.GOLD";
        if (!initialSelected.layers) initialSelected.layers = "4";
        if (!initialSelected.thickness) initialSelected.thickness = "S1000H_1.6mm";
        if (!initialSelected.copper_external) initialSelected.copper_external = "MUL_35um";
        if (!initialSelected.copper_internal) initialSelected.copper_internal = "17um";
        if (!initialSelected.stackup_method && !initialSelected.stackup) initialSelected.stackup_method = "SAFE";
        if (!initialSelected.silkscreen_color && !initialSelected.silkscreen_ink_color) initialSelected.silkscreen_color = "WHITE";
        if (!initialSelected.color_top && !initialSelected.solder_color_top) initialSelected.color_top = "GREEN";
        if (!initialSelected.color_bottom && !initialSelected.color_bot && !initialSelected.solder_color_bot) initialSelected.color_bottom = "GREEN";
        if (!initialSelected.soldermask_position && !initialSelected.position) initialSelected.soldermask_position = "TOP.BOT";
        if (!initialSelected.legend) initialSelected.legend = "WITHOUT";
        if (!initialSelected.date_position) initialSelected.date_position = "WITHOUT";
        if (!initialSelected.pcb_supplier && !initialSelected.supplier) initialSelected.pcb_supplier = "WITHOUT";
        if (!initialSelected.ipc_class && !initialSelected.ipc) initialSelected.ipc_class = "CLASS_2";

        const fallbackSelectedOfferId =
          pcb?.selected_offer_id ??
          bundle?.selected_offer_id ??
          bundle?.displayed_offer?.id ??
          pcb?.selected_offer?.id ??
          "";
        const fallbackDisplayedOffer =
          (bundle?.displayed_offer && typeof bundle.displayed_offer === "object" && bundle.displayed_offer) ||
          (pcb?.selected_offer && typeof pcb.selected_offer === "object" && pcb.selected_offer) ||
          null;
        const fallbackPrice =
          (pcb?.price_snapshot && typeof pcb.price_snapshot === "object" && pcb.price_snapshot) ||
          (bundle?.price && typeof bundle.price === "object" && bundle.price) ||
          null;
        const fallbackBundle =
          bundle ||
          (fallbackPrice || fallbackDisplayedOffer || hasMeaningfulValue(fallbackSelectedOfferId) || Object.keys(initialSelected).length
            ? {
                selected_choices: pcb?.selected_choices || initialSelected,
                selected_clean: null,
                selected_sent: null,
                price: fallbackPrice,
                offers: fallbackPrice?.offers || fallbackPrice?.delivery_items || fallbackPrice?.items || [],
                selected_offer_id: String(fallbackSelectedOfferId || ""),
                displayed_offer: fallbackDisplayedOffer,
                check: null,
                diff: null,
                overridden: [],
                invalid_selected: [],
                errors: [],
                status: null,
              }
            : null);

        if (!mounted) return;
        setSelected(initialSelected);
        setAllowDraftRestore(!hasServerSeed);
        setInitialOrderState({
          ts: bundle?.ts ?? null,
          price: fallbackPrice,
          saveBundle: fallbackBundle,
          selectedOfferId: String(fallbackSelectedOfferId || ""),
        });
        let cmsCount = asm?.cms_component_count != null ? String(asm.cms_component_count) : "";
        let thtCount = asm?.tht_component_count != null ? String(asm.tht_component_count) : "";
        let pressfitCount = asm?.press_fit_component_count != null ? String(asm.press_fit_component_count) : "";

        if (cmsCount === "" || thtCount === "" || pressfitCount === "") {
          try {
            const bomData = await getJson(`/user/projects/${projectId}/bom-items/`);
            const items = bomData?.items || [];
            let cms = 0, tht = 0, pressfit = 0;
            for (const it of items) {
              const mt = String(it?.mount_type || "").toLowerCase();
              const qty = Math.max(0, parseInt(it?.qty ?? it?.quantity ?? 0, 10) || 0);
              if (mt === "cms" || mt === "smd" || mt === "smt") cms += qty;
              else if (mt === "trad" || mt === "tht") tht += qty;
              else if (mt === "press_fit" || mt === "pressfit") pressfit += qty;
            }
            if (cmsCount === "") cmsCount = cms > 0 ? String(cms) : "";
            if (thtCount === "") thtCount = tht > 0 ? String(tht) : "";
            if (pressfitCount === "") pressfitCount = pressfit > 0 ? String(pressfit) : "";
          } catch (_) {}
        }

        setAsmCounts({ cms_component_count: cmsCount, tht_component_count: thtCount, pressfit_component_count: pressfitCount });
        const resolvedMode = provide ? "provide" : "order";
        setPcbMode(resolvedMode);
        onModeChange?.(resolvedMode);
      } catch (e) {
        console.error(e);
        if (mounted) setAllowDraftRestore(true);
        errorMsg(e);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    boot();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  async function saveConfigOrderFromOrderPayload(orderPayload) {
  const applied = orderPayload?.applied_selected || {};
  const offer = orderPayload?.offer_selected || null;
  const manualNote = String(orderPayload?.manual_note || "").trim();
  const manualReason = String(orderPayload?.manual_reason || "").trim();
  const quoteMode = String(orderPayload?.quote_mode || "").trim().toUpperCase();

  const selectedChoices = { ...(applied || {}) };
  if (quoteMode === "MANUAL") selectedChoices.quote_mode = "MANUAL";
  if (manualNote) selectedChoices.manual_note = manualNote;
  if (manualReason) selectedChoices.manual_reason = manualReason;

  const pcb_spec = {
    client_pcb_mode: "order",
    size_x: applied?.size_x ?? null,
    size_y: applied?.size_y ?? null,
    pcb_quantity: applied?.pcb_quantity ?? null,
    layers: applied?.layers ?? null,
    selected_choices: selectedChoices,
    manual_quote_note: quoteMode === "MANUAL" ? manualNote : "",
    selected_offer_id: orderPayload?.offer_selected_id || null,
    selected_offer: offer,            // snapshot (si backend accepte)
    price_snapshot: orderPayload?.price_snapshot || null,
    safe_pcb_bundle: orderPayload?.safe_pcb_bundle || null,
  };

  const assembly_spec = {
    customer_supplies_pcb: false,
    cms_component_count: asmCounts.cms_component_count !== "" ? Number(asmCounts.cms_component_count) : null,
    tht_component_count: asmCounts.tht_component_count !== "" ? Number(asmCounts.tht_component_count) : null,
    pressfit_component_count: asmCounts.pressfit_component_count !== "" ? Number(asmCounts.pressfit_component_count) : null,
  };

  await putJson(`/user/projects/${projectId}/pcb-config/`, { pcb_spec, assembly_spec });
}

  async function saveConfigProvideBasic() {
    const pcb_spec = {
      client_pcb_mode: "provide",
      size_x: selected?.size_x ?? null,
      size_y: selected?.size_y ?? null,
      pcb_quantity: selected?.pcb_quantity ?? null,
      layers: null,
      selected_choices: {},
    };
    const assembly_spec = {
      customer_supplies_pcb: true,
      cms_component_count: asmCounts.cms_component_count !== "" ? Number(asmCounts.cms_component_count) : null,
      tht_component_count: asmCounts.tht_component_count !== "" ? Number(asmCounts.tht_component_count) : null,
      pressfit_component_count: asmCounts.pressfit_component_count !== "" ? Number(asmCounts.pressfit_component_count) : null,
    };
    await putJson(`/user/projects/${projectId}/pcb-config/`, { pcb_spec, assembly_spec });
  }

  async function onModeClick(nextMode) {
    if (nextMode !== "order" && nextMode !== "provide") return;

    const nextSelected = { ...(selected || {}) };
    if (!nextSelected.family) nextSelected.family = "RIGID";
    if (!nextSelected.pcb_quantity) nextSelected.pcb_quantity = "1";
    if (!nextSelected.material) nextSelected.material = "S1000H";
    if (!nextSelected.surface_finish && !nextSelected.finish) nextSelected.surface_finish = "CHEM.GOLD";
    if (!nextSelected.layers) nextSelected.layers = "4";
    if (!nextSelected.thickness) nextSelected.thickness = "S1000H_1.6mm";
    if (!nextSelected.copper_external) nextSelected.copper_external = "MUL_35um";
    if (!nextSelected.copper_internal) nextSelected.copper_internal = "17um";
    if (!nextSelected.stackup_method && !nextSelected.stackup) nextSelected.stackup_method = "SAFE";
    if (!nextSelected.silkscreen_color && !nextSelected.silkscreen_ink_color) nextSelected.silkscreen_color = "WHITE";
    if (!nextSelected.color_top && !nextSelected.solder_color_top) nextSelected.color_top = "GREEN";
    if (!nextSelected.color_bottom && !nextSelected.color_bot && !nextSelected.solder_color_bot) nextSelected.color_bottom = "GREEN";
    if (!nextSelected.soldermask_position && !nextSelected.position) nextSelected.soldermask_position = "TOP.BOT";
    if (!nextSelected.legend) nextSelected.legend = "WITHOUT";
    if (!nextSelected.date_position) nextSelected.date_position = "WITHOUT";
    if (!nextSelected.pcb_supplier && !nextSelected.supplier) nextSelected.pcb_supplier = "WITHOUT";
    if (!nextSelected.ipc_class && !nextSelected.ipc) nextSelected.ipc_class = "CLASS_2";

    setPcbMode(nextMode);
    onModeChange?.(nextMode);

    try {
      setSaving(true);
      if (nextMode === "provide") {
        await saveConfigProvideBasic();
      } else {
        await putJson(`/user/projects/${projectId}/pcb-config/`, {
          pcb_spec: { client_pcb_mode: "order", selected_choices: nextSelected },
          assembly_spec: { customer_supplies_pcb: false },
        });
      }
    } catch (e) {
      console.error(e);
      errorMsg(e);
    } finally {
      setSaving(false);
    }
  }

  async function onContinueClick() {
  

  try {
    setSaving(true);
    if (pcbMode === "provide") {
      const can = pcbProvideRef.current?.canContinue?.();

      if (!can) {
        errorMsg("Provide: veuillez ajouter Placement et au moins Paste TOP ou Paste BOT.");
        return;
      }

      await pcbProvideRef.current?.submit?.();

      if (typeof onNext !== "function") {
        console.error("[PCB] onNext missing");
        return;
      }
      onNext("quote");
      return;
    }
    const can = pcbOrderRef.current?.canContinue?.();

    if (!can) {
      errorMsg("Order: il manque des champs ou une action requise (override).");
      return;
    }
    const orderPayload = pcbOrderRef.current?.getOrderConfigPayload?.();

    if (!orderPayload?.applied_selected || !Object.keys(orderPayload.applied_selected).length) {
      errorMsg("Order: cliquez sur Mettre à jour pour obtenir un résultat Safe-PCB.");
      return;
    }

    await saveConfigOrderFromOrderPayload(orderPayload);

    if (typeof onNext !== "function") {
      console.error("[PCB] onNext missing");
      return;
    }
    onNext("gerber");
  } catch (e) {
    console.error(e);
    errorMsg(e);
  } finally {
    setSaving(false);
  }
}

  const disabledAll = loading || saving || childBusy;

  return (
    <div className="stepper-section">
      <h2>3) PCB</h2>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
        <button
          type="button"
          className={`btn ${pcbMode === "provide" ? "primary" : ""}`}
          disabled={disabledAll}
          onClick={() => onModeClick("provide")}
        >
          Je fournis le PCB
        </button>

        <button
          type="button"
          className={`btn ${pcbMode === "order" ? "primary" : ""}`}
          disabled={disabledAll}
          onClick={() => onModeClick("order")}
        >
          Je commande le PCB
        </button>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 16 }}>
        <div className="form-row" style={{ flex: 1, minWidth: 160 }}>
          <label className="label">Nb composants CMS</label>
          <input
            type="number"
            className="input"
            min="0"
            value={asmCounts.cms_component_count}
            disabled={disabledAll}
            onChange={(e) => setAsmCounts((p) => ({ ...p, cms_component_count: e.target.value }))}
            placeholder="calculé depuis BOM"
          />
        </div>
        <div className="form-row" style={{ flex: 1, minWidth: 160 }}>
          <label className="label">Nb composants THT</label>
          <input
            type="number"
            className="input"
            min="0"
            value={asmCounts.tht_component_count}
            disabled={disabledAll}
            onChange={(e) => setAsmCounts((p) => ({ ...p, tht_component_count: e.target.value }))}
            placeholder="calculé depuis BOM"
          />
        </div>
        <div className="form-row" style={{ flex: 1, minWidth: 160 }}>
          <label className="label">Nb composants Press-fit</label>
          <input
            type="number"
            className="input"
            min="0"
            value={asmCounts.pressfit_component_count}
            disabled={disabledAll}
            onChange={(e) => setAsmCounts((p) => ({ ...p, pressfit_component_count: e.target.value }))}
            placeholder="calculé depuis BOM"
          />
        </div>
      </div>

      {(() => {
        const cms = parseInt(asmCounts.cms_component_count, 10) || 0;
        const tht = parseInt(asmCounts.tht_component_count, 10) || 0;
        const pfit = parseInt(asmCounts.pressfit_component_count, 10) || 0;
        const qty = parseInt(selected?.pcb_quantity, 10) || 1;
        const perCard = cms + tht + pfit;
        const total = perCard * qty;
        if (perCard === 0) return null;
        return (
          <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 8, fontSize: 13 }}>
            <div style={{ marginBottom: 6, fontWeight: 600, opacity: 0.85 }}>
              Composants par carte : <strong>{perCard}</strong>
              <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 10 }}>
                CMS {cms} / THT {tht} / Press-fit {pfit}
              </span>
            </div>
            <div style={{ fontWeight: 600, opacity: 0.85 }}>
              Total projet ({qty} carte{qty > 1 ? "s" : ""}) : <strong>{total}</strong>
              <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 10 }}>
                CMS {cms * qty} / THT {tht * qty} / Press-fit {pfit * qty}
              </span>
            </div>
          </div>
        );
      })()}

      <div style={{ marginTop: 14 }}>
        {pcbMode === "provide" ? (
  <StepPcbProvide
    ref={pcbProvideRef}
    projectId={projectId}
    disabled={disabledAll}
  />
) : (
  <StepPcbOrder
    ref={pcbOrderRef}
    projectId={projectId}
    disabled={disabledAll}
    onBusyChange={setChildBusy}
    initialOrderState={initialOrderState}
    allowDraftRestore={allowDraftRestore}
    selected={selected}
    setSelected={setSelected}
  />
)}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button type="button" className="btn" onClick={onPrev} disabled={disabledAll}>
          Retour
        </button>
        <button type="button" className="btn primary" onClick={onContinueClick} disabled={disabledAll}>
          {saving ? "Enregistrement..." : "Continuer"}
        </button>
      </div>
    </div>
  );
}




